import React from "react";
import CustomPopover from "../common/CustomPopover";
import { Copy, X, Check } from "lucide-react";
import { useQueryInfo } from "./services/useQueryInfo";
import clsx from "clsx";
import { useQueryPopoverStore } from "./services/useQueryPopoverStore";
import { DocumentsUsedInAnswerList } from "./DocumentsUsedInQueryAnswer";
import { useFlash } from "@/hooks/productTimerHooks";

const ANCHOR_ORIGIN = {
  vertical: "bottom",
  horizontal: "left",
} as const;

const QueryInfoPopoverBox = () => {
  const { data, reset } = useQueryPopoverStore();

  return (
    <CustomPopover
      open={Boolean(data)}
      anchorEl={data?.anchorEl || null}
      onClose={reset}
      anchorOrigin={ANCHOR_ORIGIN}
      closeOnClickOutside={true}
      childWidth={800}
    >
      {data?.qid && <QueryInfo qid={data.qid} close={reset} />}
    </CustomPopover>
  );
};

export default QueryInfoPopoverBox;

interface QueryInfoProps {
  qid: string;
  close: () => void;
}

const QueryInfo = ({ qid, close }: QueryInfoProps) => {
  const { data: queryAnswer, isLoading } = useQueryInfo(qid);
  const [copySuccess, triggerCopySuccess] = useFlash(2000);
  const hideReferencedDocs = queryAnswer?.toolId === "web_search";
  const query = isLoading
    ? "Loading..."
    : queryAnswer?.query || "No query found";
  const sections =
    queryAnswer?.toolResponse?.answer?.sectionResponses?.length || 1;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(query);
      triggerCopySuccess();
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div
      className={clsx(
        "h-[400px] w-[800px] rounded-md overflow-y-auto",
        "p-2 border-gray-500 border"
      )}
      title={query}
    >
      <span>
        <div className="flex items-center gap-2">
          <b>Prompt:</b>
          <button
            onClick={handleCopy}
            className="p-1 hover:bg-gray-100 rounded-full flex items-center gap-1"
            title={copySuccess ? "Copied!" : "Copy prompt"}
          >
            {copySuccess ? (
              <Check size={16} className="text-green-600" />
            ) : (
              <Copy size={16} />
            )}
            {copySuccess && (
              <span className="text-xs text-green-600 font-medium">
                Copied!
              </span>
            )}
          </button>
        </div>
        <div className="mt-1 pl-4 pr-2 border-l-4 text-gray-600 border-gray-500">
          {query.split("\n").map((line: string, i: number) => (
            <React.Fragment key={i}>
              {line}
              {i < query.split("\n").length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      </span>
      <hr className="m-2" />
      <DocumentsUsedInAnswerList
        qid={qid}
        hide={hideReferencedDocs}
        close={close}
      />
      {/* Eh weird gonna integrate it better later */}
      {sections > 1 ? (
        <>
          <hr className="m-2" />
          <h1>
            <b>Work Details</b>
          </h1>
          <button
            onClick={(e) => {
              //@ts-expect-error dont care
              (window as Window).openShowWorkPopover(e, { qid });
              close();
            }}
            className="text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer"
          >
            Click to see work
          </button>
        </>
      ) : null}
    </div>
  );
};
